import React, { useEffect, useRef, useState } from "react";

import {
  inputStateForValue,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingPorkPrimaryDataById,
  useMutateFootprintingPorkPrimaryData,
  usePracticeManagementPorkParameters,
  useTranslation,
} from "utils";

import { HARD_INPUT_STATES } from "consts";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin, SanityCheck } from "components";

interface PorkYieldFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const PorkYieldForm = ({ primaryDataId, readOnly, farmId, year }: PorkYieldFormProps) => {
  const { t } = useTranslation("footprintingPork");
  const formRef = useRef<FormInstance>(null);

  const [yieldKgs, setYieldKgs] = useState<number | null>(null);
  const [liveWeight, setLiveWeight] = useState<number | null>(null);

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);

  const porkPracticeQuery = usePracticeManagementPorkParameters(carbonProgram?.country?.toLowerCase() as PracticeManagement.CountryCode);
  const porkPrimaryDataQuery = useGetFootprintingPorkPrimaryDataById(primaryDataId ?? "");
  const { data: porkPrimaryData, isSuccess } = porkPrimaryDataQuery;

  const isLoading = [porkPrimaryDataQuery, porkPracticeQuery].some(queryIsLoading);
  const { mutate: mutatePorkPrimaryData } = useMutateFootprintingPorkPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      yieldKgs,
      liveWeight,
      isSubmitted: false,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePorkPrimaryData({});
      }
    },
    [isLoading, mutatePorkPrimaryData, readOnly]
  );

  useEffect(() => {
    if (porkPrimaryData && isSuccess) {
      setYieldKgs(porkPrimaryData.yieldKgs ?? null);
      setLiveWeight(porkPrimaryData.liveWeight ?? null);

      formRef.current?.setFieldsValue({
        yieldKgs: porkPrimaryData.yieldKgs ?? null,
        liveWeight: porkPrimaryData.liveWeight ?? null,
      });

      const fieldsToValidate = (["yieldKgs", "liveWeight"] as const).some((field) => porkPrimaryData[field] != null);

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [porkPrimaryData, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-pork-yield-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={24}>
          <Form.Item
            validateStatus={
              yieldKgs
                ? HARD_INPUT_STATES.includes(inputStateForValue(yieldKgs, porkPracticeQuery.data?.averageSlaughterWeightKgs))
                  ? "error"
                  : "success"
                : "success"
            }
          >
            <InputNumber
              disabled={readOnly}
              name="yieldKgs"
              dataTestId="footprinting-pork-yield-weight-input"
              label={t("porkYieldForm.question.meatYieldAmountLabel")}
              suffix={t("porkYieldForm.question.meatYieldAmountSuffix")}
              value={yieldKgs}
              initialValue={yieldKgs}
              onChange={(v) => setYieldKgs(v)}
              validateFirst
              rules={[{ type: "number" }, { required: true }]}
              guidanceText={porkPracticeQuery.data?.averageSlaughterWeightKgs}
            />
          </Form.Item>
          {yieldKgs ? <SanityCheck inputValue={yieldKgs} inputRange={porkPracticeQuery.data?.averageSlaughterWeightKgs} /> : null}
          <Form.Item
            validateStatus={
              liveWeight
                ? HARD_INPUT_STATES.includes(inputStateForValue(liveWeight, porkPracticeQuery.data?.averageLivingWeightKgs))
                  ? "error"
                  : "success"
                : "success"
            }
          >
            <InputNumber
              disabled={readOnly}
              name="liveWeight"
              dataTestId="footprinting-pork-live-weight-input"
              label={t("porkYieldForm.question.liveWeightLabel")}
              suffix={t("porkYieldForm.question.liveWeightSuffix")}
              value={liveWeight}
              initialValue={liveWeight}
              onChange={(v) => setLiveWeight(v)}
              validateFirst
              rules={[{ type: "number", required: true }]}
              guidanceText={porkPracticeQuery.data?.averageLivingWeightKgs}
            />
          </Form.Item>
          {liveWeight ? <SanityCheck inputValue={liveWeight} inputRange={porkPracticeQuery.data?.averageLivingWeightKgs} /> : null}
        </Flex>
      </Form>
    </Card>
  );
};

PorkYieldForm.Key = "pork.yield";
export { PorkYieldForm };
